// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// jsonLineDiff (#191) — pure helpers for the Compass "Both" side-by-side diff.
//
// Powers the VS-Code-style Mongo↔Convex document diff: each record is pretty-
// printed to canonical JSON (sorted keys) on both sides, then line-diffed into
// aligned rows the renderer paints red (removed / Mongo-only) and green (added /
// Convex-only). Pure + tested so the alignment/stat logic can't silently drift.
//
// canonicalize() collapses Extended-JSON wrappers ($oid/$date/$numberLong…) to
// the same primitive form the Convex sync stores, so a faithful mirror diffs to
// ZERO lines instead of lighting up on representation differences alone.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

// Collapse Mongo Extended-JSON wrappers to primitives (mirror of the sync's
// sanitize): { $oid } → hex string, { $date } → ISO string, number wrappers →
// number, { $numberDecimal } → string. Recurses through arrays + objects.
export function canonicalize(value) {
  if (value === null) return null;
  if (value === undefined) return undefined;
  if (Array.isArray(value)) return value.map(canonicalize);
  if (typeof value === "object") {
    const keys = Object.keys(value);
    if (keys.length === 1 && keys[0].startsWith("$")) {
      const k = keys[0];
      const v = value[k];
      if (k === "$oid") return String(v);
      if (k === "$date") {
        if (typeof v === "number") return new Date(v).toISOString();
        if (v && typeof v === "object" && v.$numberLong) {
          return new Date(Number(v.$numberLong)).toISOString();
        }
        return String(v);
      }
      if (k === "$numberLong" || k === "$numberInt" || k === "$numberDouble") {
        return Number(v);
      }
      if (k === "$numberDecimal") return String(v);
    }
    const out = {};
    for (const kk of keys) out[kk] = canonicalize(value[kk]);
    return out;
  }
  return value;
}

// Recursively sort object keys so two equivalent docs serialize identically.
function sortKeys(value) {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (value && typeof value === "object") {
    const out = {};
    for (const k of Object.keys(value).sort()) out[k] = sortKeys(value[k]);
    return out;
  }
  return value;
}

// Canonical, stable, pretty JSON for a document (2-space indent, sorted keys).
export function stableStringify(value) {
  return JSON.stringify(sortKeys(canonicalize(value)), null, 2);
}

// Pair a run of deletions with the following run of additions into "change"
// rows (left red ↔ right green on the same line, VS-Code style); any leftover
// becomes one-sided del/add rows.
function pairChanges(ops) {
  const rows = [];
  let i = 0;
  while (i < ops.length) {
    if (ops[i].t === "del") {
      const dels = [];
      while (i < ops.length && ops[i].t === "del") dels.push(ops[i++]);
      const adds = [];
      while (i < ops.length && ops[i].t === "add") adds.push(ops[i++]);
      const k = Math.max(dels.length, adds.length);
      for (let x = 0; x < k; x++) {
        const d = dels[x];
        const a = adds[x];
        if (d && a) rows.push({ type: "change", left: d.left, right: a.right });
        else if (d) rows.push({ type: "del", left: d.left, right: null });
        else rows.push({ type: "add", left: null, right: a.right });
      }
    } else if (ops[i].t === "add") {
      while (i < ops.length && ops[i].t === "add") {
        rows.push({ type: "add", left: null, right: ops[i++].right });
      }
    } else {
      rows.push({ type: "same", left: ops[i].left, right: ops[i].right });
      i++;
    }
  }
  return rows;
}

// LCS line diff of two strings → aligned rows. O(n*m) — fine for single docs.
export function computeLineDiff(leftStr, rightStr) {
  const a = leftStr === "" ? [] : String(leftStr).split("\n");
  const b = rightStr === "" ? [] : String(rightStr).split("\n");
  const n = a.length;
  const m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] =
        a[i] === b[j]
          ? dp[i + 1][j + 1] + 1
          : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const ops = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      ops.push({ t: "same", left: a[i], right: b[j] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      ops.push({ t: "del", left: a[i] });
      i++;
    } else {
      ops.push({ t: "add", right: b[j] });
      j++;
    }
  }
  while (i < n) ops.push({ t: "del", left: a[i++] });
  while (j < m) ops.push({ t: "add", right: b[j++] });
  return pairChanges(ops);
}

// Top-level: diff two documents into aligned rows + a small stat summary.
// A null/undefined side (record missing in that backend) → empty string, so
// the whole other side shows as added/removed.
export function diffDocuments(leftDoc, rightDoc) {
  const leftStr = leftDoc == null ? "" : stableStringify(leftDoc);
  const rightStr = rightDoc == null ? "" : stableStringify(rightDoc);
  const rows = computeLineDiff(leftStr, rightStr);
  let additions = 0;
  let deletions = 0;
  let changes = 0;
  for (const r of rows) {
    if (r.type === "add") additions++;
    else if (r.type === "del") deletions++;
    else if (r.type === "change") changes++;
  }
  return {
    rows,
    stats: {
      additions,
      deletions,
      changes,
      identical: additions === 0 && deletions === 0 && changes === 0,
    },
  };
}
